import { formatCurrency, formatQuantity, formatRate, formatPercentage } from '@/utils/formatters';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  RefreshCw, Download, Filter, X, ChevronUp, ChevronDown,
  Clock, CheckCircle2, AlertCircle, Workflow, User, Calendar,
  ArrowUpDown
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending,in_progress', label: 'Active (Pending + In Progress)' }
];

const AUTO_COMPLETE_OPTIONS = [
  { value: 'all', label: 'All Tasks' },
  { value: 'manual', label: 'Manual Only' },
  { value: 'auto', label: 'Auto-Complete Only' }
];

const SORT_OPTIONS = [
  { value: 'due_at', label: 'Due Date' },
  { value: 'created_at', label: 'Created Date' },
  { value: 'assigned_to_name', label: 'Assignee' },
  { value: 'workflow_name', label: 'Workflow' },
  { value: 'status', label: 'Status' }
];

export default function SWSTasksRegisterPage() {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [assignees, setAssignees] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    assigned_to_id: 'all',
    workflow_name: 'all',
    date_from: '',
    date_to: '',
    date_type: 'created',
    include_auto_complete: 'all',
    sort_by: 'due_at',
    sort_order: 'asc'
  });
  
  const [showFilters, setShowFilters] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.assigned_to_id && filters.assigned_to_id !== 'all') params.append('assigned_to_id', filters.assigned_to_id);
      if (filters.workflow_name && filters.workflow_name !== 'all') params.append('workflow_name', filters.workflow_name);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.date_type) params.append('date_type', filters.date_type);
      if (filters.include_auto_complete && filters.include_auto_complete !== 'all') params.append('include_auto_complete', filters.include_auto_complete);
      params.append('sort_by', filters.sort_by);
      params.append('sort_order', filters.sort_order);
      
      const response = await axios.get(
        `${API_URL}/sws-tasks/register/all?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      
      setTasks(response.data.tasks || []);
      setTotal(response.data.total || 0);
      setStatusCounts(response.data.status_counts || {});
      setAssignees(response.data.filters?.assignees || []);
      setWorkflows(response.data.filters?.workflows || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. You need SWS or MIS module access.');
      } else {
        toast.error('Failed to load tasks');
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.assigned_to_id) params.append('assigned_to_id', filters.assigned_to_id);
      if (filters.workflow_name) params.append('workflow_name', filters.workflow_name);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await axios.get(
        `${API_URL}/sws-tasks/register/export?${params.toString()}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sws_tasks_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export');
    }
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      assigned_to_id: 'all',
      workflow_name: 'all',
      date_from: '',
      date_to: '',
      date_type: 'created',
      include_auto_complete: 'all',
      sort_by: 'due_at',
      sort_order: 'asc'
    });
  };

  const toggleSortOrder = () => {
    setFilters(prev => ({
      ...prev,
      sort_order: prev.sort_order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const openTaskDetails = (task) => {
    setSelectedTask(task);
    setDetailsOpen(true);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const getStatusBadge = (task) => {
    const isTaskOverdue = isOverdue(task);
    const isLateCompletion = task.status === 'completed' && task.due_at && task.completed_at && 
                             new Date(task.completed_at) > new Date(task.due_at);
    
    if (task.status === 'completed') {
      if (isLateCompletion) {
        return 'bg-red-50 text-red-600 border-red-200'; // Light red for late completion
      }
      return 'bg-green-100 text-green-800 border-green-300'; // Green for on-time
    }
    
    if (isTaskOverdue) {
      return 'bg-red-100 text-red-800 border-red-300'; // Red for overdue
    }
    
    return 'bg-yellow-100 text-yellow-800 border-yellow-300'; // Yellow for pending
  };

  const getStatusLabel = (task) => {
    const isTaskOverdue = isOverdue(task);
    
    if (task.status === 'completed') {
      return 'Completed';
    }
    
    if (isTaskOverdue) {
      return 'Overdue';
    }
    
    return 'Pending';
  };

  const isOverdue = (task) => {
    if (task.status === 'completed') return false;
    if (!task.due_at) return false;
    return new Date(task.due_at) < new Date();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">SWS Tasks Register</h1>
            <p className="text-muted-foreground">View and manage all workflow tasks</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="w-4 h-4 mr-1" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchTasks}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="w-4 h-4 mr-1" />
              Export
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statusCounts.pending || 0}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Workflow className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statusCounts.in_progress || 0}</p>
                  <p className="text-xs text-muted-foreground">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statusCounts.completed || 0}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{total}</p>
                  <p className="text-xs text-muted-foreground">Total (filtered)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        {showFilters && (
          <Card>
            <CardHeader className="py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Filters</CardTitle>
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>
            </CardHeader>
            <CardContent className="py-3">
              <div className="grid grid-cols-6 gap-4">
                {/* Status */}
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select
                    value={filters.status}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Assignee */}
                <div>
                  <Label className="text-xs">Assigned To</Label>
                  <Select
                    value={filters.assigned_to_id}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, assigned_to_id: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Assignees" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Assignees</SelectItem>
                      {assignees.map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Workflow */}
                <div>
                  <Label className="text-xs">Workflow</Label>
                  <Select
                    value={filters.workflow_name}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, workflow_name: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Workflows" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Workflows</SelectItem>
                      {workflows.map(w => (
                        <SelectItem key={w} value={w}>{w}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date From */}
                <div>
                  <Label className="text-xs">Date From</Label>
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                    className="h-9"
                  />
                </div>

                {/* Date To */}
                <div>
                  <Label className="text-xs">Date To</Label>
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                    className="h-9"
                  />
                </div>

                {/* Auto Complete */}
                <div>
                  <Label className="text-xs">Task Type</Label>
                  <Select
                    value={filters.include_auto_complete}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, include_auto_complete: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Tasks" />
                    </SelectTrigger>
                    <SelectContent>
                      {AUTO_COMPLETE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Sort Controls */}
              <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Sort By:</Label>
                  <Select
                    value={filters.sort_by}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, sort_by: v }))}
                  >
                    <SelectTrigger className="h-8 w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SORT_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" onClick={toggleSortOrder}>
                  {filters.sort_order === 'asc' ? (
                    <><ChevronUp className="w-4 h-4 mr-1" /> Ascending</>
                  ) : (
                    <><ChevronDown className="w-4 h-4 mr-1" /> Descending</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tasks Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin mr-2" />
                <span>Loading tasks...</span>
              </div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Workflow className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No tasks found matching your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Task #</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead className="w-[150px]">Workflow</TableHead>
                      <TableHead className="w-[150px]">Assigned To</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead className="w-[150px]">Due Date/Time</TableHead>
                      <TableHead className="w-[150px]">Completed At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tasks.map((task) => (
                      <TableRow 
                        key={task.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => openTaskDetails(task)}
                      >
                        <TableCell className="font-mono text-xs">{task.task_number || '-'}</TableCell>
                        <TableCell>
                          <div className="max-w-[300px]">
                            <p className="font-medium truncate">{task.title}</p>
                            {task.description && (
                              <p className="text-xs text-muted-foreground truncate">{task.description}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">{task.workflow_name}</TableCell>
                        <TableCell className="text-xs">{task.assigned_to_name}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${getStatusBadge(task)}`}>
                            {getStatusLabel(task)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          {formatDateTime(task.due_at)}
                        </TableCell>
                        <TableCell className="text-xs">
                          {formatDateTime(task.completed_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Showing count */}
        {!loading && tasks.length > 0 && (
          <p className="text-sm text-muted-foreground text-center">
            Showing {tasks.length} of {total} tasks
          </p>
        )}
      </div>

      {/* Task Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Task Details</DialogTitle>
            <DialogDescription>{selectedTask?.task_number}</DialogDescription>
          </DialogHeader>
          
          {selectedTask && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Title</Label>
                <p className="font-medium">{selectedTask.title}</p>
              </div>
              
              {selectedTask.description && (
                <div>
                  <Label className="text-xs text-muted-foreground">Description</Label>
                  <p className="text-sm">{selectedTask.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Workflow</Label>
                  <p className="text-sm">{selectedTask.workflow_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Badge className={getStatusBadge(selectedTask)}>{getStatusLabel(selectedTask)}</Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Assigned To</Label>
                  <p className="text-sm">{selectedTask.assigned_to_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Due Date/Time</Label>
                  <p className="text-sm">{formatDateTime(selectedTask.due_at)}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Created At</Label>
                  <p className="text-sm">{formatDateTime(selectedTask.created_at)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Completed At</Label>
                  <p className="text-sm">{formatDateTime(selectedTask.completed_at)}</p>
                </div>
              </div>
              
              {selectedTask.trigger_data && (
                <div className="pt-4 border-t">
                  <Label className="text-xs text-muted-foreground">Related Entity</Label>
                  <p className="text-sm font-mono">
                    {selectedTask.trigger_data.gate_entry_no || 
                     selectedTask.trigger_data.item_code_request_no || 
                     selectedTask.related_entity_id || '-'}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
